import { useEffect, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import NFTCard from './NFTCard';
import { SFUT_LEGACY_COLLECTION, SFUT_LEGACY_GALLERY_URL } from '../data/sfutLegacy';
import { SFUT_LEGACY_THUMB_SIZE } from '../data/sfutLegacyManifest';
import {
  getSfutLegacyPicksSync,
  legacyImage,
  loadSfutLegacyPicks,
  type LegacyItem,
} from '../web3/sfutLegacyFeed';

type SfutLegacyShowcaseProps = {
  maxItems?: number;
  compact?: boolean;
  showCta?: boolean;
};

/**
 * SFUT Legacy strip. Data comes from sfutLegacyFeed: prefetched/cached picks render
 * immediately; otherwise it loads at once (the visitor just opened the tab).
 */
const SfutLegacyShowcase = ({
  maxItems = 5,
  compact = false,
  showCta = true,
}: SfutLegacyShowcaseProps) => {
  const [nfts, setNfts] = useState<LegacyItem[]>(() => getSfutLegacyPicksSync(maxItems) ?? []);
  const [loading, setLoading] = useState(() => nfts.length === 0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ready = getSfutLegacyPicksSync(maxItems);
    if (ready) {
      setNfts(ready);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    loadSfutLegacyPicks(maxItems)
      .then((picked) => {
        if (cancelled) return;
        setNfts(picked);
        if (!picked.length) setError('No NFTs');
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Unable to load NFTs');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [maxItems]);

  const cta = (
    <a
      href={SFUT_LEGACY_GALLERY_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="sfut-legacy-gallery-cta"
      className="inline-flex min-h-11 items-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-3 font-medium text-white shadow-lg transition-all hover:from-blue-700 hover:to-purple-700 hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
    >
      <span>Open SFUT Legacy gallery</span>
      <ExternalLink size={16} aria-hidden />
    </a>
  );

  return (
    <div className={compact ? 'space-y-6' : 'space-y-8'} data-testid="sfut-legacy-showcase">
      {showCta ? <div className="text-center">{cta}</div> : null}

      {error ? (
        <p className="text-center text-sm text-white/80" role="status">
          Gallery is live on OpenPlaza even if this preview did not load.
        </p>
      ) : null}

      {loading && nfts.length === 0 ? (
        <div
          className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5"
          aria-hidden
        >
          {Array.from({ length: maxItems }).map((_, index) => (
            <div
              key={index}
              className="aspect-square animate-pulse rounded-lg bg-white/10"
            />
          ))}
        </div>
      ) : null}

      {nfts.length > 0 ? (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
          {nfts.map((nft, index) => {
            const image = legacyImage(nft);
            return (
              <NFTCard
                key={`${nft.id}-${index}`}
                nft={nft}
                delay={index * 0.08}
                collectionAddress={SFUT_LEGACY_COLLECTION}
                ctaLabel="View"
                imageSrc={image.src}
                fallbackImageSrc={image.fallback}
                imageSize={SFUT_LEGACY_THUMB_SIZE}
              />
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export default SfutLegacyShowcase;
